import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SidebarLayout, PageHeader, Card } from '../components/layout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../hooks/use-auth';
import { useNeedsResolvedOrders, useReprocessApprovers, useRestartWorkflow } from '../hooks/use-orders';
import { useUserApprovals } from '../hooks/use-user-approvals';
import { useMinDurationFlag } from '../hooks/use-min-duration-flag';
import { OrderSummaryDto } from '../types';
import { formatCurrency, formatDate, sortableHeaderProps } from '../utils';
import { OrderService } from '../services/order-service';
import httpClient from '../services/http-client';

export function NeedsResolvedOrdersPage() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [showFilters, setShowFilters] = useState(false);
  const [processingOrderId, setProcessingOrderId] = useState<string | null>(null);

  // Get division ID from user approvals hook for consistency
  const { divisionId } = useUserApprovals();

  // Filter state
  const [filters, setFilters] = useState({
    page: 1,
    limit: 50,
    search: '',
    sortBy: 'updatedAt',
    sortOrder: 'desc' as 'asc' | 'desc'
  });

  // Fetch orders with NeedsResolved status
  const { 
    data: needsResolvedData, 
    isLoading, 
    isFetching,
    error, 
    refetch 
  } = useNeedsResolvedOrders(
    divisionId || '', 
    filters,
    !!divisionId && isAuthenticated
  );
  const showFetchingOverlay = useMinDurationFlag(isFetching && !isLoading);

  const needsResolvedOrders = needsResolvedData?.items || [];
  const totalCount = needsResolvedData?.totalCount || 0;
  const currentPage = needsResolvedData?.page || 1;
  const pageSize = needsResolvedData?.pageSize || 50;
  const totalPages = needsResolvedData?.totalPages || 1;

  // Reprocess mutation
  const reprocessMutation = useReprocessApprovers();
  const restartMutation = useRestartWorkflow();

  const handleSort = (field: string) => {
    setFilters(prev => ({
      ...prev,
      sortBy: field,
      sortOrder: prev.sortBy === field && prev.sortOrder === 'asc' ? 'desc' : 'asc',
      page: 1
    }));
  };

  const getSortIcon = (field: string) => {
    if (filters.sortBy !== field) return <i className="fa-solid fa-sort text-gray-500 dark:text-secondary-400 ml-2"></i>;
    return filters.sortOrder === 'asc' 
      ? <i className="fa-solid fa-sort-up text-blue-600 dark:text-blue-400 ml-2"></i>
      : <i className="fa-solid fa-sort-down text-blue-600 dark:text-blue-400 ml-2"></i>;
  };

  const handleReprocess = async (orderId: string) => {
    if (!divisionId) return;
    
    setProcessingOrderId(orderId);
    try {
      await reprocessMutation.mutateAsync({ divisionId, orderId });
      // Show success message
      alert('Reprocess signal sent successfully. The workflow will retry approver resolution.');
      // Refetch the list after a short delay to see status changes
      setTimeout(() => {
        refetch();
      }, 2000);
    } catch (error: any) {
      console.error('Error reprocessing order:', error);
      
      // Check if the error is because the workflow doesn't exist
      const errorData = error?.response?.data || error?.data || error;
      const errorCode = errorData?.errorCode || '';
      
      if (errorCode === 'WORKFLOW_NOT_FOUND') {
        // Offer to restart the workflow
        const shouldRestart = window.confirm(
          'The workflow for this order no longer exists (it may have completed or crashed before the NeedsResolved feature was implemented).\n\n' +
          'Would you like to restart the workflow? This will create a new workflow that will wait for approver configuration to be fixed.'
        );
        
        if (shouldRestart) {
          try {
            const result = await restartMutation.mutateAsync({ divisionId, orderId });
            alert(`Workflow restarted successfully!\nWorkflow ID: ${result.workflowId}\n\nThe order is now in a wait state and will process once configuration is fixed.`);
            setTimeout(() => {
              refetch();
            }, 2000);
          } catch (restartError: any) {
            console.error('Error restarting workflow:', restartError);
            const restartErrorData = restartError?.response?.data || restartError?.data || {};
            const restartStatusCode = restartError?.response?.status;
            const restartErrorCode = restartErrorData?.errorCode;

            if (restartStatusCode === 409 || restartErrorCode === 'WORKFLOW_ALREADY_RUNNING') {
              const temporalStatus = restartErrorData?.temporalStatus || 'Running';
              const temporalRunId = restartErrorData?.temporalRunId;
              const details = temporalRunId
                ? `\nStatus: ${temporalStatus}\nRun ID: ${temporalRunId}`
                : `\nStatus: ${temporalStatus}`;

              alert(
                `Workflow is already running for this order.${details}\n\nNo restart was performed. The order list will refresh now.`
              );
              refetch();
              return;
            }

            const restartErrorMsg = restartErrorData?.message || restartError?.message || 'Failed to restart workflow';
            alert(`Error restarting workflow: ${restartErrorMsg}`);
          }
        }
      } else {
        // Generic error message
        const errorMessage = errorData?.message || error?.message || 'Failed to send reprocess signal';
        alert(`Error: ${errorMessage}`);
      }
    } finally {
      setProcessingOrderId(null);
    }
  };

  const handleViewDetails = (orderId: string) => {
    navigate(`/order-detail/${orderId}`);
  };

  const handleOpenInSimulator = async (orderId: string) => {
    if (!divisionId) return;
    
    try {
      const orderService = new OrderService(httpClient);
      const orderDetail = await orderService.getOrderDetail(divisionId, orderId);
      
      if (orderDetail.orderDocument) {
        // Navigate to simulator with the order JSON
        navigate(`/simulator/${divisionId}`, {
          state: {
            jsonData: typeof orderDetail.orderDocument === 'string' 
              ? orderDetail.orderDocument 
              : JSON.stringify(orderDetail.orderDocument, null, 2),
            orderNumber: orderDetail.orderNumber
          }
        });
      } else {
        alert('No JSON document found for this order');
      }
    } catch (error: any) {
      console.error('Error fetching order document:', error);
      alert(`Error loading order document: ${error?.message || 'Unknown error'}`);
    }
  };

  const handleRefresh = () => {
    refetch();
  };

  if (!isAuthenticated) {
    return (
      <SidebarLayout>
        <div className="py-4">
          <Card>
            <div className="p-6 text-center">
              <p className="text-gray-600 dark:text-secondary-400">Please sign in to view orders.</p>
            </div>
          </Card>
        </div>
      </SidebarLayout>
    );
  }

  if (error) {
    return (
      <SidebarLayout>
        <div className="py-4">
          <PageHeader
            title="Orders Needing Resolution"
            subtitle="Orders that require configuration changes to proceed"
          />
          <Card>
            <div className="p-6 text-center">
              <p className="text-red-600 dark:text-red-400 mb-4">Error loading orders</p>
              <Button onClick={() => refetch()}>Try Again</Button>
            </div>
          </Card>
        </div>
      </SidebarLayout>
    );
  };

  return (
    <SidebarLayout>
      <div className="py-6">
        <PageHeader
          title="Error Queue"
          subtitle="Orders that require configuration changes or manual intervention to continue"
        />

        {/* Info Banner */}
        <div className="mb-6 bg-yellow-50 dark:bg-yellow-950 border-l-4 border-yellow-400 p-4 rounded">
          <div className="flex">
            <div className="flex-shrink-0">
              <i className="fa-solid fa-exclamation-triangle text-yellow-400 text-xl"></i>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Configuration Required
              </h3>
              <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-400">
                <p>
                  These orders have no valid approvers due to match identifier configuration issues. 
                  To resolve:
                </p>
                <ol className="list-decimal list-inside mt-2 space-y-1">
                  <li>Review the order details to identify required match identifiers</li>
                  <li>Ensure users have ALL required match identifiers configured</li>
                  <li>Click "Reprocess" to retry approver resolution</li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-6 mb-6">
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-700 dark:text-secondary-200">Needs Resolution</h3>
                <p className="text-3xl font-bold text-yellow-600 mt-2">{totalCount}</p>
              </div>
              <div className="bg-yellow-100 dark:bg-yellow-900 p-3 rounded-full">
                <i className="fa-solid fa-exclamation-triangle text-yellow-600 text-xl"></i>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-700 dark:text-secondary-200">Processing</h3>
                <p className="text-3xl font-bold text-blue-600 dark:text-blue-400 mt-2">
                  {processingOrderId ? '1' : '0'}
                </p>
              </div>
              <div className="bg-blue-100 dark:bg-blue-900 p-3 rounded-full">
                <i className="fa-solid fa-spinner text-blue-600 dark:text-blue-400 text-xl"></i>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-700 dark:text-secondary-200">Displaying</h3>
                <p className="text-3xl font-bold text-gray-600 dark:text-secondary-400 mt-2">{needsResolvedOrders.length}</p>
              </div>
              <div className="bg-gray-100 dark:bg-secondary-700 p-3 rounded-full">
                <i className="fa-solid fa-list text-gray-600 dark:text-secondary-400 text-xl"></i>
              </div>
            </div>
          </Card>
        </div>

        {/* Orders Table */}
        <Card>
          <div className="p-6 border-b flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-secondary-100">Orders Needing Resolution</h2>
            <div className="flex space-x-2">
              <Button onClick={() => refetch()} variant="outline">
                <i className="fa-solid fa-refresh mr-2"></i>
                Refresh
              </Button>
              <Button 
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
              >
                <i className="fa-solid fa-filter mr-2"></i>
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </Button>
            </div>
          </div>

          {/* Filter Section */}
          {showFilters && (
            <div className="p-6 border-b bg-gray-50 dark:bg-secondary-900">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-1">
                    Search
                  </label>
                  <input
                    type="text"
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value, page: 1 }))}
                    placeholder="Search by order number..."
                    className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-1">
                    Sort By
                  </label>
                  <select
                    value={filters.sortBy}
                    onChange={(e) => setFilters(prev => ({ ...prev, sortBy: e.target.value, page: 1 }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                  >
                    <option value="updatedAt">Last Updated</option>
                    <option value="orderNumber">Order Number</option>
                    <option value="totalAmount">Amount</option>
                    <option value="createdAt">Submitted Date</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-1">
                    Page Size
                  </label>
                  <select
                    value={filters.limit}
                    onChange={(e) => setFilters(prev => ({ ...prev, limit: Number(e.target.value), page: 1 }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                  >
                    <option value="10">10 per page</option>
                    <option value="25">25 per page</option>
                    <option value="50">50 per page</option>
                    <option value="100">100 per page</option>
                  </select>
                </div>
              </div>
              {filters.search && (
                <div className="mt-3 flex items-center justify-between">
                  <p className="text-sm text-gray-600 dark:text-secondary-400">
                    {totalCount} results found
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setFilters({
                        page: 1,
                        limit: 50,
                        search: '',
                        sortBy: 'updatedAt',
                        sortOrder: 'desc'
                      });
                    }}
                  >
                    Clear Filters
                  </Button>
                </div>
              )}
            </div>
          )}

          <div className="p-6 relative">
            {/* Loading overlay during filter changes */}
            {showFetchingOverlay && (
              <div className="absolute inset-0 bg-white/60 dark:bg-secondary-800/60 flex items-start justify-center pt-16 z-10 rounded-b-lg">
                <div className="bg-white dark:bg-secondary-800 shadow-lg rounded-lg px-4 py-3 flex items-center gap-3">
                  <i className="fa-solid fa-spinner fa-spin text-blue-600 dark:text-blue-400"></i>
                  <span className="text-sm font-medium text-gray-700 dark:text-secondary-200">Updating results...</span>
                </div>
              </div>
            )}
            {isLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600 dark:text-secondary-400">Loading orders...</p>
              </div>
            ) : needsResolvedOrders.length === 0 ? (
              <div className="text-center py-8">
                <i className="fa-solid fa-check-circle text-4xl text-green-300 mb-4"></i>
                <p className="text-gray-600 dark:text-secondary-400 mb-2">No orders need resolution</p>
                <p className="text-sm text-gray-500 dark:text-secondary-400">
                  {filters.search 
                    ? 'Try adjusting your search terms' 
                    : 'All orders have valid approvers configured'}
                </p>
              </div>
            ) : (
              <>
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-gray-500 dark:text-secondary-400">
                      <th 
                        className="pb-4 cursor-pointer hover:text-gray-700 dark:hover:text-secondary-200"
                        {...sortableHeaderProps(() => handleSort('orderNumber'), filters.sortBy === 'orderNumber', filters.sortOrder)}
                      >
                        Order {getSortIcon('orderNumber')}
                      </th>
                      <th className="pb-4">Submitter</th>
                      <th 
                        className="pb-4 cursor-pointer hover:text-gray-700 dark:hover:text-secondary-200"
                        {...sortableHeaderProps(() => handleSort('totalAmount'), filters.sortBy === 'totalAmount', filters.sortOrder)}
                      >
                        Amount {getSortIcon('totalAmount')}
                      </th>
                      <th 
                        className="pb-4 cursor-pointer hover:text-gray-700 dark:hover:text-secondary-200"
                        {...sortableHeaderProps(() => handleSort('updatedAt'), filters.sortBy === 'updatedAt', filters.sortOrder)}
                      >
                        Last Updated {getSortIcon('updatedAt')}
                      </th>
                      <th className="pb-4">Status</th>
                      <th className="pb-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {needsResolvedOrders.map((order) => (
                      <tr key={order.id} className="border-t" data-testid={`order-row-${order.id}`}>
                        <td className="py-4">
                          <div>
                            <a
                              className="font-mono text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 hover:underline cursor-pointer"
                              href={`/order-detail/${order.id}`}
                              onClick={(e) => { e.preventDefault(); handleViewDetails(order.id); }}
                              title="Click to view order details"
                              data-testid={`order-number-${order.id}`}
                            >
                              {order.orderNumber}
                            </a>
                            <div className="text-sm text-gray-500 dark:text-secondary-400">{order.title}</div>
                          </div>
                        </td>
                        <td className="py-4">
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white">{order.submitterName}</div>
                            <div className="text-sm text-gray-500 dark:text-secondary-400">{order.divisionName}</div>
                          </div>
                        </td>
                        <td className="py-4 font-medium">
                          {formatCurrency(order.totalAmount, order.currency)}
                        </td>
                        <td className="py-4">
                          {formatDate(order.updatedAt)}
                        </td>
                        <td className="py-4">
                          <Badge variant="warning">
                            <i className="fa-solid fa-exclamation-triangle mr-1"></i>
                            Needs Resolved
                          </Badge>
                        </td>
                        <td className="py-4">
                          <div className="flex justify-end space-x-2">
                            <a
                              className="text-blue-600 dark:text-blue-400 hover:text-blue-800 p-1"
                              href={`/order-detail/${order.id}`}
                              onClick={(e) => { e.preventDefault(); handleViewDetails(order.id); }}
                              title="View Details"
                              data-testid={`view-details-btn-${order.id}`}
                            >
                              <i className="fa-solid fa-eye"></i>
                            </a>
                            <button 
                              className="text-purple-600 dark:text-purple-400 hover:text-purple-800 p-1"
                              onClick={() => handleOpenInSimulator(order.id)}
                              title="Open in Workflow Simulator"
                            >
                              <i className="fa-solid fa-flask"></i>
                            </button>
                            <Button
                              size="sm"
                              onClick={() => handleReprocess(order.id)}
                              disabled={processingOrderId === order.id}
                              data-testid={`reprocess-btn-${order.id}`}
                            >
                              {processingOrderId === order.id ? (
                                <>
                                  <i className="fa-solid fa-spinner fa-spin mr-1"></i>
                                  Processing...
                                </>
                              ) : (
                                <>
                                  <i className="fa-solid fa-refresh mr-1"></i>
                                  Reprocess
                                </>
                              )}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-6 flex items-center justify-between">
                    <div className="text-sm text-gray-700 dark:text-secondary-200">
                      Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} results
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setFilters(prev => ({ ...prev, page: Math.max(1, currentPage - 1) }))}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </Button>
                      <span className="px-3 py-1 text-sm text-gray-700 dark:text-secondary-200">
                        Page {currentPage} of {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setFilters(prev => ({ ...prev, page: Math.min(totalPages, currentPage + 1) }))}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </Card>
      </div>
    </SidebarLayout>
  );
}
